import { Link, useLocation } from "react-router-dom";
import { useCallback, useEffect, useRef, useState, Fragment, type JSX } from "react";
import "./home.page.css";
import Logo from "../assets/logo_text.png";
import TrophyLogo from "../assets/logo.png";
import NewsletterComponent from "../components/newsletter.component";
import SponsorsComponent from "../components/sponsors.component";
import NewsCardsComponent from "../components/newsCards.component";
import BackToTopComponent from "../components/backToTop.component";
import { fetchPhase, fetchBusinessCaseDeadline, fetchCategories, fetchSiteContent, type AwardPhase, type PhaseState, type Category } from "../api/tcg";
import { parseLayout } from "../data/siteContent.default";
import { useLanguage } from "../i18n/language.context";
import LanguageDropdown from "../components/languageDropdown.component";

const timelineSteps = [
    { label: "01", titleKey: "ui.timeline.1.title", textKey: "ui.timeline.1.text" },
    { label: "02", titleKey: "ui.timeline.2.title", textKey: "ui.timeline.2.text" },
    { label: "03", titleKey: "ui.timeline.3.title", textKey: "ui.timeline.3.text" },
    { label: "04", titleKey: "ui.timeline.4.title", textKey: "ui.timeline.4.text" },
];

const infoCards = [
    { titleKey: "ui.info.1.title", textKey: "ui.info.1.text" },
    { titleKey: "ui.info.2.title", textKey: "ui.info.2.text" },
    { titleKey: "ui.info.3.title", textKey: "ui.info.3.text" },
];

type TimeLeft = { days: number; hours: number; minutes: number; seconds: number };
type CounterConfig = { enabled: boolean; targetDate: string; label: string };

function calcTimeLeft(targetDate: string): TimeLeft {
    const diff = new Date(targetDate).getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / 1000 / 60) % 60),
        seconds: Math.floor((diff / 1000) % 60),
    };
}

function CountdownTimer({ config }: { config: CounterConfig }) {
    const [timeLeft, setTimeLeft] = useState<TimeLeft>(() => calcTimeLeft(config.targetDate));

    useEffect(() => {
        const id = setInterval(() => setTimeLeft(calcTimeLeft(config.targetDate)), 1000);
        return () => clearInterval(id);
    }, [config.targetDate]);

    if (!config.enabled) return null;

    const units = [
        { v: timeLeft.days, l: "d" },
        { v: timeLeft.hours, l: "h" },
        { v: timeLeft.minutes, l: "m" },
        { v: timeLeft.seconds, l: "s" },
    ];

    return (
        <div className="hero-countdown">
            <div className="hero-countdown-units">
                {units.map(({ v, l }, i) => (
                    <div className="hero-countdown-unit-wrap" key={l}>
                        <div className="hero-count-unit">
                            <strong>{String(v).padStart(2, "0")}</strong>
                            <span>{l}</span>
                        </div>
                        {i < units.length - 1 && <span className="hero-count-sep">:</span>}
                    </div>
                ))}
            </div>
            <p className="hero-countdown-label">{config.label}</p>
        </div>
    );
}

function BackendCountdown() {
    const { t: tr } = useLanguage();
    const [phase, setPhase] = useState<PhaseState | null>(null);

    useEffect(() => {
        fetchPhase().then(setPhase).catch(() => setPhase(null));
    }, []);

    if (!phase) return null;

    const now = Date.now();

    if (phase.phase === "NOMINATION" && phase.nomination_ends_at) {
        return <CountdownTimer config={{ enabled: true, targetDate: phase.nomination_ends_at, label: tr("ui.cd.nominationEnds") }} />;
    }

    if (phase.phase === "VOTING" && phase.voting_ends_at) {
        return <CountdownTimer config={{ enabled: true, targetDate: phase.voting_ends_at, label: tr("ui.cd.votingEnds") }} />;
    }

    const upcoming: { date: string; label: string }[] = [];

    if (phase.nomination_starts_at && new Date(phase.nomination_starts_at).getTime() > now) {
        upcoming.push({ date: phase.nomination_starts_at, label: tr("ui.cd.nominationStarts") });
    }

    if (phase.voting_starts_at && new Date(phase.voting_starts_at).getTime() > now) {
        upcoming.push({ date: phase.voting_starts_at, label: tr("ui.cd.votingStarts") });
    }

    if (upcoming.length > 0) {
        upcoming.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        return <CountdownTimer config={{ enabled: true, targetDate: upcoming[0].date, label: upcoming[0].label }} />;
    }

    return <p className="hero-countdown-label">{tr("ui.cd.noPhase")}</p>;
}

function BusinessCaseCountdown() {
    const { t: tr } = useLanguage();
    const [data, setData] = useState<{ starts_at: string | null; deadline: string | null } | null>(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        fetchBusinessCaseDeadline()
            .then((d) => setData({ starts_at: d.starts_at, deadline: d.deadline }))
            .catch(() => setData(null))
            .finally(() => setLoaded(true));
    }, []);

    if (!loaded) return null;
    if (!data || !data.deadline) return <p className="hero-countdown-label">{tr("ui.cd.bcNoDeadline")}</p>;

    const now = Date.now();
    const start = data.starts_at ? new Date(data.starts_at).getTime() : null;
    const end = new Date(data.deadline).getTime();

    if (start !== null && now < start) {
        return <CountdownTimer config={{ enabled: true, targetDate: data.starts_at!, label: tr("ui.cd.bcOpens") }} />;
    }

    if (now >= end) return <p className="hero-countdown-label">{tr("ui.cd.bcClosed")}</p>;

    return <CountdownTimer config={{ enabled: true, targetDate: data.deadline, label: tr("ui.cd.bcRemaining") }} />;
}

function SectionHeading(props: Readonly<{ eyebrow: string; title: string; text: string }>) {
    return (
        <div className="section-heading">
            <span>{props.eyebrow}</span>
            <h2>{props.title}</h2>
            <p>{props.text}</p>
        </div>
    );
}

const CATEGORY_GROUPS = [
    { key: "product", titleKey: "ui.group.product.title", subtitleKey: "ui.group.product.subtitle", votable: true },
    { key: "community", titleKey: "ui.group.community.title", subtitleKey: "ui.group.community.subtitle", votable: true },
    { key: "jury", titleKey: "ui.group.jury.title", subtitleKey: "ui.group.jury.subtitle", votable: false },
    { key: "performance", titleKey: "ui.group.performance.title", subtitleKey: "ui.group.performance.subtitle", votable: false },
];

function HomePage() {
    const location = useLocation();
    const handledHash = useRef<string | null>(null);

    const updateHash = useCallback((id: string | null) => {
        const base = `${window.location.pathname}${window.location.search}`;
        window.history.replaceState(null, "", id ? `${base}#${encodeURIComponent(id)}` : base);
    }, []);

    const scrollTo = useCallback((id: string, options: { updateHash?: boolean } = {}) => {
        const el = document.getElementById(id);
        if (!el) return;

        const nav = document.querySelector<HTMLElement>(".home-nav");
        const offset = (nav?.offsetHeight ?? 0) + 24;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;

        if (options.updateHash !== false) {
            updateHash(id);
            handledHash.current = `#${id}`;
        }

        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }, [updateHash]);

    const [categories, setCategories] = useState<Category[]>([]);
    const { t: tr, lang } = useLanguage();

    useEffect(() => {
        fetchCategories(lang).then(setCategories).catch(() => setCategories([]));
    }, [lang]);

    const [content, setContent] = useState<Record<string, string>>({});

    useEffect(() => {
        fetchSiteContent(lang).then(setContent).catch(() => setContent({}));
    }, [lang]);

    const [awardPhase, setAwardPhase] = useState<AwardPhase>("IDLE");

    useEffect(() => {
        fetchPhase()
            .then((phase) => setAwardPhase(phase.phase))
            .catch(() => setAwardPhase("IDLE"));
    }, []);

    const awardNavKey = awardPhase === "NOMINATION"
        ? "ui.nav.nomination"
        : awardPhase === "VOTING"
            ? "ui.nav.voting"
            : "ui.nav.awards";

    const heroAwardCtaKey = awardPhase === "NOMINATION"
        ? "ui.hero.nominateNow"
        : awardPhase === "VOTING"
            ? "ui.hero.voteNow"
            : "ui.hero.exploreAwards";

    const t = (key: string) => {
        const v = content[key];
        return v && v !== "" ? v : tr(key);
    };

    const layout = parseLayout(content["layout.sections"]);

    useEffect(() => {
        if (!location.hash) return;
        if (handledHash.current === location.hash) return;

        const targetId = decodeURIComponent(location.hash.slice(1));
        handledHash.current = location.hash;
        const timeout = window.setTimeout(() => scrollTo(targetId, { updateHash: false }), 0);

        return () => window.clearTimeout(timeout);
    }, [location.hash, scrollTo]);

    const sections: Record<string, () => JSX.Element> = {
        introStrip: () => (
            <section className="intro-strip">
                <div><span>{tr("ui.intro.1a")}</span><strong>{tr("ui.intro.1b")}</strong></div>
                <div><span>{tr("ui.intro.2a")}</span><strong>{tr("ui.intro.2b")}</strong></div>
                <div><span>{tr("ui.intro.3a")}</span><strong>{tr("ui.intro.3b")}</strong></div>
            </section>
        ),
        voteProcess: () => (
            <section className="home-section vote-process" id="vote-process">
                <SectionHeading eyebrow={t("process.eyebrow")} title={t("process.title")} text={t("process.text")} />
                <div className="timeline-grid">
                    {timelineSteps.map((step, i) => (
                        <article className="timeline-card" key={step.label}>
                            <div className="timeline-card-header">
                                <div className="timeline-step-badge">{step.label}</div>
                                {i < timelineSteps.length - 1 && <div className="timeline-connector" aria-hidden="true" />}
                            </div>
                            <h3>{tr(step.titleKey)}</h3>
                            <p>{tr(step.textKey)}</p>
                        </article>
                    ))}
                </div>
            </section>
        ),
        awardCategories: () => (
            <section className="home-section award-categories" id="award-categories">
                <SectionHeading eyebrow={t("categories.eyebrow")} title={t("categories.title")} text={t("categories.text")} />
                <div className="award-group-grid">
                    {CATEGORY_GROUPS.map((group) => {
                        const cats = categories.filter((c) => (c.group_key ?? "community") === group.key);
                        if (cats.length === 0) return null;

                        return (
                            <article className="award-group-card" key={group.key}>
                                <div className="award-group-header">
                                    <span>{tr(group.subtitleKey)}</span>
                                    <h3>{tr(group.titleKey)}</h3>
                                </div>
                                <div className="category-list">
                                    {cats.map((c) => (
                                        <Link to={`/voting/${c.slug}`} className="category-pill" key={c.id}>
                                            <img src={TrophyLogo} alt="" aria-hidden="true" />
                                            <span>{c.name}</span>
                                        </Link>
                                    ))}
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>
        ),
        aboutAwards: () => (
            <section className="home-section about-awards" id="about-awards">
                <SectionHeading eyebrow={t("about.eyebrow")} title={t("about.title")} text={t("about.text")} />
                <div className="about-stats-strip">
                    <div className="about-stat"><strong>13</strong><span>{tr("ui.about.stat.categories")}</span></div>
                    <div className="about-stat"><strong>2</strong><span>{tr("ui.about.stat.phases")}</span></div>
                    <div className="about-stat"><strong>Global</strong><span>{tr("ui.about.stat.reach")}</span></div>
                    <div className="about-stat"><strong>2026</strong><span>{tr("ui.about.stat.edition")}</span></div>
                </div>
                <div className="info-card-grid">
                    {infoCards.map((card) => (
                        <article className="info-card" key={card.titleKey}>
                            <h3>{tr(card.titleKey)}</h3>
                            <p>{tr(card.textKey)}</p>
                        </article>
                    ))}
                </div>
            </section>
        ),
        businessCase: () => (
            <section className="home-section business-case" id="business-case">
                <div className="business-copy">
                    <div className="home-eyebrow">{t("business.eyebrow")}</div>
                    <h2>{t("business.title")}</h2>
                    <p>{t("business.text")}</p>
                </div>
                <div className="business-card">
                    <ul>
                        <li>{tr("ui.business.li1")}</li>
                        <li>{tr("ui.business.li2")}</li>
                        <li>{tr("ui.business.li3")}</li>
                    </ul>
                    <Link className="primary-cta" to="/business-case">{tr("ui.business.submit")}</Link>
                </div>
            </section>
        ),
        partners: () => (
            <section className="home-section partners-section"><SponsorsComponent /></section>
        ),
        newsSocials: () => (
            <section className="home-section instagram-section" id="news">
                <SectionHeading eyebrow={t("news.eyebrow")} title={t("news.title")} text={t("news.text")} />
                <NewsCardsComponent />
            </section>
        ),
        newsletter: () => (
            <section className="home-section newsletter-section" id="newsletter">
                <div className="newsletter-intro">
                    <SectionHeading eyebrow={t("newsletter.eyebrow")} title={t("newsletter.title")} text={t("newsletter.text")} />
                </div>
                <NewsletterComponent />
            </section>
        ),
    };

    return (
        <div id="home-page" className="default-page max">
            <nav className="home-nav" aria-label="Home navigation">
                <div className="home-nav-left">
                    <Link to="/" className="home-nav-logo no-hover" onClick={() => { updateHash(null); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
                        <img src={Logo} alt="TCG Awards" />
                    </Link>
                </div>
                <div className="home-nav-center">
                    <button type="button" onClick={() => scrollTo("about-awards")}>{tr("ui.nav.about")}</button>
                    <button type="button" onClick={() => scrollTo("award-categories")}>{tr(awardNavKey)}</button>
                    <button type="button" onClick={() => scrollTo("business-case")}>{tr("ui.nav.businessCases")}</button>
                    <button type="button" onClick={() => scrollTo("news")}>{tr("ui.nav.news")}</button>
                </div>
                <div className="home-nav-right">
                    <LanguageDropdown />
                </div>
            </nav>

            <section className="home-hero" id="top">
                <div className="home-hero-content">
                    <div className="home-eyebrow">{t("hero.eyebrow")}</div>
                    <h1>{t("hero.title")}</h1>
                    <p>{t("hero.text")}</p>
                    <div className="home-hero-actions">
                        <button className="primary-cta" type="button" onClick={() => scrollTo("award-categories")}>{tr(heroAwardCtaKey)}</button>
                        <button className="secondary-cta" type="button" onClick={() => scrollTo("vote-process")}>{tr("ui.hero.seeTimeline")}</button>
                    </div>
                </div>
                <div className="home-hero-panel" aria-label="Voting overview">
                    <div className="hero-counter"><span>13</span><p>{tr("ui.hero.categoriesOpen")}</p></div>
                    <BackendCountdown />
                    <BusinessCaseCountdown />
                    <div className="hero-counter-grid">
                        <div><strong>2</strong><span>{tr("ui.hero.phases")}</span></div>
                        <div><strong>1</strong><span>{tr("ui.hero.globalCommunity")}</span></div>
                    </div>
                </div>
            </section>

            {layout.filter((s) => s.visible).map((s) => (
                <Fragment key={s.key}>{sections[s.key] ? sections[s.key]() : null}</Fragment>
            ))}

            <BackToTopComponent />
        </div>
    );
}

export default HomePage;
